import { useNavigate } from 'react-router-dom';
import { useForm } from '../../hooks/useForm';
import { FormField, FormTextArea, SmartFormActions } from '../shared/form';
import type { Prompt } from '../../types';

export interface PromptFormData {
  name: string;
  content: string;
  isActive: string;
}

interface PromptFormProps {
  initialData?: Partial<Prompt>;
  onSubmit: (data: any) => Promise<void>;
  onCancel?: () => void;
  mode?: 'create' | 'edit';
}

export function PromptForm({
  initialData,
  onSubmit,
  onCancel,
  mode = 'create',
}: PromptFormProps) {
  const navigate = useNavigate();

  const {
    values,
    errors,
    isSubmitting,
    isDirty,
    isValid,
    handleChange,
    handleSubmit,
  } = useForm<PromptFormData>({
    initialValues: {
      name: initialData?.name || '',
      content: initialData?.content || '',
      isActive: initialData?.isActive || 'true',
    },
    onSubmit: async (formValues) => {
      const data = {
        name: formValues.name,
        content: formValues.content,
        isActive: formValues.isActive,
      };

      await onSubmit(data);
    },
    validate: (values) => {
      const errors: Partial<Record<keyof PromptFormData, string>> = {};
      if (!values.name.trim()) {
        errors.name = 'Name is required';
      }
      if (!values.content.trim()) {
        errors.content = 'Content is required';
      }
      return errors;
    },
    requiredFields: ['name', 'content'],
  });

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    } else {
      navigate('/prompts');
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="space-y-6">
        <FormField
          label="Name"
          name="name"
          type="text"
          value={values.name}
          onChange={handleChange}
          error={errors.name}
          required
          placeholder="Enter prompt name"
          disabled={mode === 'edit'}
        />

        <FormTextArea
          label="Content"
          name="content"
          value={values.content}
          onChange={handleChange}
          error={errors.content}
          required
          placeholder="Enter prompt content"
          rows={12}
        />

        <div className="flex items-center">
          <input
            type="checkbox"
            id="isActive"
            name="isActive"
            checked={values.isActive === 'true'}
            onChange={(e) => handleChange({ target: { name: 'isActive', value: e.target.checked ? 'true' : 'false' } } as any)}
            className="h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
          />
          <label htmlFor="isActive" className="ml-2 block text-sm text-gray-700 dark:text-gray-300">
            Active
          </label>
        </div>
      </div>

      <SmartFormActions
        mode={mode}
        isDirty={isDirty}
        isValid={isValid}
        isSubmitting={isSubmitting}
        onCancel={handleCancel}
        submitLabel={mode === 'create' ? 'Create Prompt' : 'Save Changes'}
      />
    </form>
  );
}
